import type { ColumnDef, ColumnSizingState } from '@tanstack/react-table'
import type { Opportunity } from '@/types'
import { Link } from 'react-router-dom'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { DealHealthBadge } from '@/components/DealHealthScore'
import { WinProbabilityBadge } from '@/components/WinProbability'
import { formatDate, formatCurrency } from '@/lib/utils'

const getStageVariant = (stage?: string): 'default' | 'secondary' | 'success' | 'warning' => {
  const normalized = stage?.toLowerCase()
  if (normalized?.includes('closed won')) return 'success'
  if (normalized?.includes('closed lost')) return 'secondary'
  if (normalized?.includes('quote') || normalized?.includes('verbal')) return 'warning'
  if (normalized?.includes('presentation')) return 'secondary'
  return 'default'
}

interface CreateOpportunitiesColumnsOptions {
  onEditOpportunity?: (opportunityId: string) => void
  onDeleteOpportunity?: (opportunityId: string, name: string) => void
  /** Current user's id — Delete is shown only on rows this user owns (unless isAdmin). */
  currentUserId?: string
  /** Admins/Super Admins may delete any opportunity. */
  isAdmin?: boolean
  selectedIds?: Set<string>
  onToggleSelect?: (id: string) => void
  onToggleSelectAll?: () => void
  allSelected?: boolean
}

export const createOpportunitiesColumns = (
  options: CreateOpportunitiesColumnsOptions = {}
): ColumnDef<Opportunity>[] => [
  {
    id: 'select',
    header: () => (
      <Checkbox
        checked={options.allSelected}
        onCheckedChange={options.onToggleSelectAll}
        aria-label="Select all opportunities"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={options.selectedIds?.has(row.original.id) ?? false}
        onCheckedChange={() => options.onToggleSelect?.(row.original.id)}
        aria-label={`Select opportunity ${row.original.name}`}
      />
    ),
    size: 50,
    minSize: 50,
    maxSize: 50,
    enableResizing: false,
  },
  {
    id: 'name',
    header: 'Opportunity Name',
    accessorKey: 'name',
    meta: { sortField: 'name' },
    size: 250,
    cell: ({ row }) => (
      <div className="flex items-center gap-2 min-w-0">
        <Link
          to={`/opportunities/${row.original.id}`}
          className="font-medium text-primary hover:underline focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 truncate"
        >
          {row.original.name}
        </Link>
        {row.original.has_task === false && (
          <Badge
            variant="outline"
            className="text-[10px] px-1.5 py-0 bg-[#941052] text-white border-[#941052] whitespace-nowrap"
          >
            No Task
          </Badge>
        )}
      </div>
    ),
  },
  {
    id: 'account_name',
    header: 'Account Name',
    accessorKey: 'account_name',
    meta: { sortField: 'account_name' },
    size: 200,
    cell: ({ row }) =>
      row.original.account_id && row.original.account_name ? (
        <Link
          to={`/accounts/${row.original.account_id}`}
          className="text-primary hover:underline focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
          aria-label={`View account: ${row.original.account_name}`}
        >
          {row.original.account_name}
        </Link>
      ) : (
        <span>{row.original.account_name || '-'}</span>
      ),
  },
  {
    id: 'amount',
    header: 'Amount (Source)',
    accessorKey: 'amount',
    // Preserve the source-currency display, but sort monetary values in the
    // canonical reporting currency so mixed-currency rows order correctly.
    meta: { sortField: 'amount_cad' },
    size: 150,
    cell: ({ row }) =>
      row.original.amount
        ? formatCurrency(row.original.amount, row.original.currency_iso_code || 'CAD')
        : '-',
  },
  {
    id: 'close_date',
    header: 'Close Date',
    accessorKey: 'close_date',
    meta: { sortField: 'close_date' },
    size: 150,
    cell: ({ row }) => (row.original.close_date ? formatDate(row.original.close_date) : '-'),
  },
  {
    id: 'next_task_date',
    header: 'Next Task',
    accessorKey: 'next_task_date',
    meta: { sortField: 'next_task_date' },
    size: 130,
    cell: ({ row }) => {
      const date = (row.original as any).next_task_date
      if (!date) return <span className="text-muted-foreground">-</span>
      const d = new Date(date)
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      const isToday = d.toDateString() === today.toDateString()
      return <span className={isToday ? 'font-medium text-primary' : ''}>{formatDate(date)}</span>
    },
  },
  {
    id: 'stage',
    header: 'Stage',
    accessorKey: 'stage',
    meta: { sortField: 'stage_name' },
    size: 150,
    cell: ({ row }) =>
      row.original.stage ? (
        <Badge variant={getStageVariant(row.original.stage)}>{row.original.stage}</Badge>
      ) : null,
  },
  {
    id: 'health',
    header: 'Health',
    accessorKey: 'health_score',
    size: 80,
    cell: ({ row }) =>
      !row.original.is_closed ? <DealHealthBadge score={row.original.health_score} /> : null,
  },
  {
    id: 'win_probability',
    header: 'Win %',
    accessorKey: 'win_probability',
    size: 80,
    cell: ({ row }) =>
      !row.original.is_closed ? (
        <WinProbabilityBadge probability={row.original.win_probability} />
      ) : null,
  },
  {
    id: 'owner_name',
    header: 'Owner',
    accessorKey: 'owner_name',
    meta: { sortField: 'owner_name' },
    size: 150,
    cell: ({ row }) => (
      <span className="truncate block max-w-[150px]">
        {(row.original as any).owner_name || '-'}
      </span>
    ),
  },
  {
    id: 'actions',
    header: () => <div className="text-right">Actions</div>,
    size: 210,
    minSize: 180,
    maxSize: 250,
    enableResizing: false,
    cell: ({ row }) => (
      <div className="text-right flex gap-2 justify-end">
        <Button
          variant="ghost"
          size="sm"
          aria-label={`View opportunity ${row.original.name}`}
          asChild
        >
          <Link to={`/opportunities/${row.original.id}`}>View</Link>
        </Button>
        {options.onEditOpportunity && (
          <Button
            variant="ghost"
            size="sm"
            aria-label={`Edit opportunity ${row.original.name}`}
            onClick={() => options.onEditOpportunity!(row.original.id)}
          >
            Edit
          </Button>
        )}
        {options.onDeleteOpportunity &&
          (options.isAdmin || row.original.owner_id === options.currentUserId) && (
            <Button
              variant="ghost"
              size="sm"
              aria-label={`Delete opportunity ${row.original.name}`}
              onClick={() => options.onDeleteOpportunity!(row.original.id, row.original.name ?? '')}
            >
              Delete
            </Button>
          )}
      </div>
    ),
  },
]

export const opportunitiesDefaultColumnSizing: ColumnSizingState = {
  select: 50,
  name: 250,
  account_name: 200,
  amount: 150,
  close_date: 150,
  stage: 150,
  health: 80,
  win_probability: 80,
  owner_name: 150,
  actions: 210,
}
